// xeroParse — read a Xero financial export (.xlsx) into normalised rows.
//
// Pure and React-free on purpose: app/scripts/verify-eom.mjs runs this under
// plain node against a committed fixture, which is the only regression test
// this repo can offer (no test runner in package.json).
//
// REPORT-TYPE AGNOSTIC. James hasn't sent the real export yet, so nothing here
// asserts which Xero report this is. The parser discovers structure rather than
// matching a known layout, and no user-facing string names a report type — see
// the note on readPreamble(). When the real file arrives this should read it
// without changes; only accountMap.js should need editing.
//
// The shape it discovers:
//   Row 1..n  preamble — org name, report title, period label (all optional)
//   Row h     header   — account column + one or more period columns
//   Row h+1.. body     — section headers, indented accounts, subtotal rows
//
// exceljs is already a dependency and its browser build reads as well as
// writes (workbook.xlsx.load), so there's no new package here.

import ExcelJS from 'exceljs';

// Tagged errors so the UI can show a specific, actionable message instead of
// a stack trace. `code` is the contract; `message` is the human sentence.
export class XeroParseError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'XeroParseError';
    this.code = code;
  }
}

const HEADER_SCAN_ROWS = 20;   // preamble is never longer than this in practice
const MAX_PREAMBLE_CELLS = 4;  // preamble rows are sparse; header rows are wide

// ── cell readers ───────────────────────────────────────────────────────

// Flatten any exceljs cell value to a plain string.
// Handles the shapes exceljs actually returns: primitives, { result } for
// formulas, { richText } for styled runs, { text } for hyperlinks, Date.
function cellText(cell) {
  if (!cell) return '';
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    if (Array.isArray(v.richText)) return v.richText.map(r => r.text || '').join('').trim();
    if (v.text != null) return String(v.text).trim();
    if (v.result != null) return String(v.result).trim();
    if (v.formula != null) return '';
  }
  return String(v).trim();
}

/**
 * Coerce a cell to a number, or null when there is no figure.
 *
 * Covers every shape a Xero export produces:
 *   1234.5            plain number
 *   { result: 1234 }  formula cell
 *   "$1,234.50"       currency-formatted text
 *   "(1,234.50)"      accounting negative → -1234.50
 *   "-" / "" / "n/a"  no value → null
 *
 * Returns null rather than 0 for absent values: "this account has no figure
 * this period" and "this account is exactly zero" must stay distinguishable,
 * otherwise the reconciliation check can't tell a parse miss from a real zero.
 */
export function cellNumber(cell) {
  if (!cell) return null;
  let v = cell.value;
  if (v == null) return null;

  if (typeof v === 'object' && !(v instanceof Date)) {
    if (typeof v.result === 'number') return v.result;
    if (v.result != null) v = v.result;
    else if (Array.isArray(v.richText)) v = v.richText.map(r => r.text || '').join('');
    else if (v.text != null) v = v.text;
    else return null;
  }

  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;

  let s = v.trim();
  if (!s || s === '-' || s === '–' || s === '—' || /^n\/?a$/i.test(s)) return null;

  // Accounting negatives: (1,234.50) means -1234.50
  let negative = false;
  if (/^\(.*\)$/.test(s)) { negative = true; s = s.slice(1, -1); }

  // Strip currency symbols, thousands separators, spaces (incl. non-breaking)
  s = s.replace(/[$£€¥]/g, '').replace(/,/g, '').replace(/[\s ]/g, '');

  // Trailing/leading sign
  if (s.endsWith('-')) { negative = true; s = s.slice(0, -1); }
  if (s.startsWith('-')) { negative = true; s = s.slice(1); }

  const pct = s.endsWith('%');
  if (pct) s = s.slice(0, -1);

  if (!s || !/^\d*\.?\d+$/.test(s)) return null;
  let n = parseFloat(s);
  if (!Number.isFinite(n)) return null;
  if (pct) n = n / 100;
  return negative ? -n : n;
}

// Does this cell hold a period column label? Xero writes these as real dates,
// as "Jun-26" / "Jun 2026", or as "Total" / "YTD" for aggregate columns.
function isPeriodHeader(cell) {
  if (!cell || cell.value == null) return false;
  if (cell.value instanceof Date) return true;
  const t = cellText(cell);
  if (!t) return false;
  return (
    /^[A-Za-z]{3,9}[-\s]?\d{2,4}$/.test(t) ||          // Jun-26, June 2026
    /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(t) ||        // 30/06/2026
    /^(total|ytd|year to date|budget|actual|variance)$/i.test(t) ||
    /\b(19|20)\d{2}\b/.test(t)                          // anything carrying a year
  );
}

// Stable, human-readable key for a period column.
function periodKey(cell, colNumber) {
  if (cell?.value instanceof Date) {
    const d = cell.value;
    return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  }
  const t = cellText(cell);
  return t || `Column ${colNumber}`;
}

// How deeply is this row indented? Xero uses cell indent for the account
// tree, but plain leading spaces show up in some export paths, so take both.
function indentOf(cell) {
  if (!cell) return 0;
  const styleIndent = cell.alignment?.indent || 0;
  const raw = typeof cell.value === 'string' ? cell.value : '';
  const spaceIndent = Math.floor((raw.match(/^ */)?.[0].length || 0) / 2);
  return Math.max(styleIndent, spaceIndent);
}

function isBold(cell) {
  if (!cell) return false;
  if (cell.font?.bold) return true;
  const v = cell.value;
  if (v && typeof v === 'object' && Array.isArray(v.richText)) {
    return v.richText.some(r => r.font?.bold);
  }
  return false;
}

// ── main ───────────────────────────────────────────────────────────────

/**
 * Parse a Xero financial export.
 *
 * @param {ArrayBuffer|Buffer} buf  raw .xlsx bytes
 * @returns {Promise<{ meta, periods, rows }>}
 *   meta    { orgName, reportTitle, periodLabel, sheetName }
 *   periods [{ key, colNumber }]           report columns, in sheet order
 *   rows    [{ rowIndex, label, indent, kind, bold, values, section }]
 *           kind: 'section' | 'account' | 'total' | 'blank'
 *           values: { [periodKey]: number|null }
 *           section: nearest enclosing section label (null at top level)
 *
 * @throws {XeroParseError} NOT_A_WORKBOOK | EMPTY_WORKBOOK
 *                          NO_PERIOD_COLUMNS | NO_ACCOUNT_ROWS
 */
export async function parseXeroWorkbook(buf) {
  const wb = new ExcelJS.Workbook();
  try {
    await wb.xlsx.load(buf);
  } catch (err) {
    throw new XeroParseError(
      'NOT_A_WORKBOOK',
      'That file isn\'t a readable Excel workbook. Export your financial data from Xero in Excel (.xlsx) format and try again.',
    );
  }

  const populated = wb.worksheets.filter(s => s.rowCount > 0);
  if (!populated.length) {
    throw new XeroParseError('EMPTY_WORKBOOK', 'The workbook has no sheets with any content in them.');
  }

  // Find the sheet that actually holds the figures rather than assuming it's
  // the first one. A cover//contents sheet in front of the data is common —
  // people add them, and Xero's multi-report exports produce several tabs — so
  // walk every populated sheet and take the first that yields both a header
  // with period columns AND account rows underneath it. Anything less would
  // reject a perfectly good workbook because of a title page.
  let picked = null;
  for (const sheet of populated) {
    const header = findHeaderRow(sheet);
    if (!header) continue;
    const rows = readBody(sheet, header);
    if (!rows.some(r => r.kind === 'account')) continue;
    picked = { sheet, header, rows };
    break;
  }

  if (!picked) {
    // Distinguish "no figures anywhere" from "figures but no account rows", so
    // the message points at the actual problem.
    const anyHeader = populated.some(s => !!findHeaderRow(s));
    throw anyHeader
      ? new XeroParseError(
        'NO_ACCOUNT_ROWS',
        'The workbook has columns of figures but no account rows underneath them. Check the export includes the account detail, not just totals.',
      )
      : new XeroParseError(
        'NO_PERIOD_COLUMNS',
        'Couldn\'t find a column of figures in this workbook. A financial export needs account names down the page and at least one column of amounts — this file may be a different kind of export.',
      );
  }

  const { sheet, header, rows } = picked;
  const meta = readPreamble(sheet, header.rowIndex);
  meta.sheetName = sheet.name;

  return { meta, periods: header.periods, rows };
}

// Find the header row: the row that defines the period columns.
//
// Preferred signal is a cell literally reading "Account". Failing that (Xero
// sometimes leaves that cell blank), take the widest row in the scan window
// that has period-shaped headers AND is followed by numeric data — that
// combination is what distinguishes a header from a stray preamble line.
function findHeaderRow(ws) {
  const limit = Math.min(ws.rowCount, HEADER_SCAN_ROWS);
  let best = null;

  for (let r = 1; r <= limit; r++) {
    const row = ws.getRow(r);
    const periods = [];
    let labelCol = null;

    row.eachCell({ includeEmpty: false }, (cell, col) => {
      if (isPeriodHeader(cell)) { periods.push({ key: periodKey(cell, col), colNumber: col }); return; }
      const t = cellText(cell);
      if (t && labelCol == null) labelCol = col;
    });

    if (!periods.length) continue;
    if (!hasNumericRowBelow(ws, r)) continue;

    const explicitAccount = /^account/i.test(cellText(row.getCell(labelCol ?? 1)));
    const candidate = { rowIndex: r, labelCol: labelCol ?? 1, periods, score: periods.length + (explicitAccount ? 100 : 0) };
    if (explicitAccount) return candidate;
    if (!best || candidate.score > best.score) best = candidate;
  }

  return best;
}

// Are there numeric figures in the ~10 rows below `r`? Guards against
// treating a preamble date line ("For the month ended 30 June 2026") as the
// header just because it contains a year.
function hasNumericRowBelow(ws, r) {
  const stop = Math.min(ws.rowCount, r + 10);
  for (let i = r + 1; i <= stop; i++) {
    const row = ws.getRow(i);
    let found = false;
    row.eachCell({ includeEmpty: false }, (cell) => {
      if (!found && cellNumber(cell) != null) found = true;
    });
    if (found) return true;
  }
  return false;
}

// Pull org name / report title / period label out of the rows above the
// header. Xero's order is org, then title, then the date-range line.
//
// The title regex below RECOGNISES common Xero report names so we can locate
// the title row — it does not require one. Whatever title the export actually
// carries is passed through verbatim and displayed as-is; the fallback is the
// neutral 'Financial Report'. So a different Xero report type still reads
// correctly and still labels itself honestly.
function readPreamble(ws, headerRowIndex) {
  const lines = [];
  for (let r = 1; r < headerRowIndex; r++) {
    const row = ws.getRow(r);
    const texts = [];
    row.eachCell({ includeEmpty: false }, (cell) => {
      const t = cellText(cell);
      if (t) texts.push(t);
    });
    if (texts.length && texts.length <= MAX_PREAMBLE_CELLS) lines.push(texts.join(' · '));
  }

  // Broad enough to find the title row across Xero's report types without
  // committing the module to any one of them.
  const titleIdx = lines.findIndex(l => /profit (and|&) loss|income statement|balance sheet|trial balance|budget (variance|summary)|financial (report|statement)|statement of/i.test(l));
  const periodIdx = lines.findIndex(l => /^(for|period|month|year|as at|as of)\b/i.test(l) || /\bto\b.*\b(19|20)\d{2}/i.test(l));

  return {
    orgName: titleIdx > 0 ? lines[0] : (lines[0] && titleIdx !== 0 ? lines[0] : ''),
    reportTitle: titleIdx >= 0 ? lines[titleIdx] : 'Financial Report',
    periodLabel: periodIdx >= 0 ? lines[periodIdx] : (lines[lines.length - 1] || ''),
  };
}

// Walk every row below the header, classifying each one and tracking the
// enclosing section so accountMap can use section context when mapping.
function readBody(ws, header) {
  const rows = [];
  let currentSection = null;

  for (let r = header.rowIndex + 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const labelCell = row.getCell(header.labelCol);
    const label = cellText(labelCell).trim();

    const values = {};
    let hasValue = false;
    for (const p of header.periods) {
      const n = cellNumber(row.getCell(p.colNumber));
      values[p.key] = n;
      if (n != null) hasValue = true;
    }

    if (!label && !hasValue) {
      rows.push({ rowIndex: r, label: '', indent: 0, kind: 'blank', bold: false, values, section: currentSection });
      continue;
    }

    const bold = isBold(labelCell);
    const indent = indentOf(labelCell);
    const isTotal = /^total\b/i.test(label) || /^(gross|net|operating)\s+(profit|loss|surplus)/i.test(label);

    let kind;
    if (isTotal) {
      kind = 'total';
    } else if (!hasValue && label) {
      // A label with no figures is a section header. This is also where a
      // new section begins for everything below it.
      kind = 'section';
      currentSection = label;
    } else {
      kind = 'account';
    }

    rows.push({ rowIndex: r, label, indent, kind, bold, values, section: currentSection });

    // A "Total X" row closes section X — anything after it belongs to the
    // next block, not the one that just ended.
    if (kind === 'total' && currentSection && new RegExp(`^total\\s+${escapeRe(currentSection)}`, 'i').test(label)) {
      currentSection = null;
    }
  }

  return rows;
}

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Find a report-level total row by name, e.g. 'Net Profit'.
 * Used by reportModel's reconciliation check to get Xero's own bottom line.
 * Searches bottom-up: the authoritative Net Profit is the last one.
 */
export function findTotalRow(rows, patterns) {
  const res = Array.isArray(patterns) ? patterns : [patterns];
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.kind !== 'total' && row.kind !== 'account') continue;
    if (res.some(re => re.test(row.label))) return row;
  }
  return null;
}
